import React from "react";

import { useState } from "react";
import { Snackbar, Alert, Button, Tooltip } from "@mui/material";
import { Col } from "react-bootstrap";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import OpenNewIcon from "@mui/icons-material/OpenInNewOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import GridItem from "../../partials/gridItem";
import ListItem from "../../partials/listItem";
import FavouriteBtn from "../../partials/favouriteBtn";
import { useBreakpointWidth } from "../../partials/windowSize";
import { client, publishedAssetUrl } from "../../config";
import { logCustomEvent } from "../../core/analytics";
import { plainText } from "../../utils/helpers";

export default function LinkItem(props) {
  const [snackOpen, setSnackOpen] = useState(false);
  const { width } = useBreakpointWidth();

  const copyToClipboard = (string) => {
    navigator.clipboard.writeText(string);
    setSnackOpen(true);
  };

  const iconSize = "0.9rem";

  let listItem = {
    id: props.item?.id,
    title: props.item?.title,
    category_label: props.item?.category_labels,
    className: "link",
  };

  // The v3 links API exposes the target as `field_website` (LinksApiResource
  // buildRow); the older node shape used `url`. Several handlers below checked
  // only `url`, which v3 never returns — so clicking a link did nothing. One
  // resolver, used everywhere, keeps both shapes working.
  const linkTarget = (item) => item?.field_website || item?.url || "";

  const openLink = (item) => {
    const target = linkTarget(item);
    if (!target) {
      // Nothing to open — surface it rather than failing silently, since a
      // link row with no target looks identical to a broken click handler.
      console.warn("[link] no target on item", item?.id, item);
      return;
    }
    // Open via a synthetic anchor rather than window.open(): a bare
    // window.open(url, "_blank") is treated as a popup by Chrome/Safari in
    // some contexts and is then blocked silently — no error, no new tab,
    // indistinguishable from a dead click handler. An anchor click is a
    // genuine user navigation and is not popup-blocked.
    const a = document.createElement("a");
    a.href = target;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    logCustomEvent("read", item, "Link");
  };

  // What the info affordance would show: the summary, falling back to the
  // body text. Empty means there is nothing to open — both the grid tooltip
  // and the list accordion key their disabled state off this single value, so
  // the button can never be enabled over an empty panel.
  const infoText =
    plainText(props.item?.summary) || plainText(props.item?.body);

  // The link preview LinkPreviewFetcher generates. Root-relative from the
  // list/CRUD endpoints, published-dir relative once the row comes from
  // book.json — publishedAssetUrl resolves either, which the hand-built
  // assetBase + "/link-thumbnails/<id>.jpg" path this replaced could not.
  const imgSrc = publishedAssetUrl("link", props.item?.id, props.item?.thumbnail);

  if (props.creator) {
    return (
      <>
        <GridItem
          xs={12}
          sm={6}
          lg={3}
          xl={3}
          xxl={2}
          itemClass="link"
          item={props.item}
          bundle={props.item?.bundle}
          id={props.item?.id}
          title={props.item?.title}
          category_label={props.item.category_labels}
          created={props.item?.created}
          hasImg={true}
          imgSrc={imgSrc}
          alt={`Poster for Link ${props.item.id}`}
          cardActionArea={true}
          creator={props.creator}
          cardActionCallBack={() => openLink(props.item)}
          contentType={props.contentType}
          viewOverride={linkTarget(props.item)}
          shareEnabled
          route={"documents"}
          field_access_token={props.item.access_token}
          sharePrompt={`This is a public link that enables you to share the document with your client, no login required.`}
        ></GridItem>
      </>
    );
  } else {
    return (
      <>
        {props.gridView && (
          <GridItem
            xs={12}
            md={3}
            itemClass="link"
            bundle={props.item?.bundle}
            title={props.item?.title}
            // Reserved unconditionally, as news/galleries/videos do. Only
            // about half the links have a generated preview, and making the
            // slot conditional left cards in the same row with their titles
            // and action bars at different heights.
            hasImg={true}
            imgSrc={imgSrc}
            alt={`Poster for Link ${props.item?.id}`}
            cardActionArea={true}
            cardActionCallBack={() => openLink(props.item)}
            cardActions={true}
            category_label={props.item?.category_labels}
          >
            {client !== "equans" && (
              <Col xs={"auto"} className="d-flex align-items-center">
                <Tooltip title={infoText} placement="bottom">
                  <Button
                    variant="screenItemBtn"
                    mode="default"
                    sx={
                      infoText
                        ? {}
                        : {
                            pointerEvents: "none",
                            background: "transparent",
                            color: "rgba(0, 0, 0, 0.26)",
                          }
                    }
                  >
                    <InfoIcon sx={{ fontSize: iconSize }} />
                  </Button>
                </Tooltip>
              </Col>
            )}
            <Col xs={"auto"} className="d-flex align-items-center">
              <Button
                variant="screenItemBtn"
                mode="default"
                onClick={() => {
                  const target = linkTarget(props.item);
                  if (target) copyToClipboard(target);
                }}
              >
                <CopyIcon sx={{ fontSize: iconSize }} />
              </Button>
            </Col>
            <Col xs={"auto"} className="d-flex align-items-center">
              <Button
                variant="screenItemBtn"
                mode="default"
                onClick={() => openLink(props.item)}
              >
                <OpenNewIcon sx={{ fontSize: iconSize }} />
              </Button>
            </Col>
            <FavouriteBtn
              id={props.item.id}
              contentType={props.contentType}
              mode={"default"}
              title={props.item?.title}
              thumbnail={imgSrc}
            />
          </GridItem>
        )}

        {!props.gridView && (
          <ListItem
            active={props.activeDetail === props.item?.id}
            owner="client"
            item={listItem}
            idEnabled={width < 576 ? false : true}
            categoryEnabled={width < 768 ? false : true}
            category_label={props.item?.category_labels}
            infoBtn={client === "equans" ? false : true}
            handleInfo={() => props.setActiveDetails(props.item?.id)}
            infoDisabled={!infoText}
            accordInfo
            accordInfoBody={infoText}
            copyBtn
            handleCopy={() => {
              const target = linkTarget(props.item);
              if (target) copyToClipboard(target);
            }}
            openBtn
            openBtnDisabled={false}
            handleOpen={() => openLink(props.item)}
            contentType={props.contentType}
          ></ListItem>
        )}
        <Snackbar
          open={snackOpen}
          autoHideDuration={3000}
          onClose={() => setSnackOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackOpen(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            Link copied!
          </Alert>
        </Snackbar>
      </>
    );
  }
}
